/**
 * The cells Contacten and Organisaties show in the same way: a dash for nothing, an owner, a
 * category, a person who did something, a moment, a day. Components only (the
 * `react-refresh/only-export-components` rule); the column maps are in `contactColumns.tsx` and
 * `organisationColumns.tsx`.
 */
import { EntityAvatar } from '@/components/ui/entity-avatar'
import { formatDateTimeLong, formatRelatief } from '@/lib/format'
import { CategoryChip } from '../components/CategoryChip'
import { todayIso } from '../dates'
import { formatShortDate } from '../query/dateRanges'
import type { CrmCategoryRef, CrmOwner } from '../types'
import { shortName } from './shortName'

export function Dash() {
  return <span className="text-muted-foreground">—</span>
}

/** Text, or a dash when there is none. */
export function PlainText({ value }: { value: string | number | null | undefined }) {
  return value === null || value === undefined || value === '' ? <Dash /> : <span className="text-muted-foreground">{value}</span>
}

export function OwnerCell({ owner }: { owner: CrmOwner | null }) {
  return <PlainText value={owner?.full_name} />
}

export function CategoryCell({ category }: { category: CrmCategoryRef | null }) {
  return category ? <CategoryChip category={category} /> : <Dash />
}

/** Someone who did something: a small avatar and the first name. */
export function PersonName({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-sm">
      <EntityAvatar kind="person" name={name} size="xs" />
      <span className="truncate" title={name}>
        {shortName(name)}
      </span>
    </span>
  )
}

/** A moment as "3 dagen geleden", with the full date and time in the tooltip and for a screen reader. */
export function RelativeTime({ iso }: { iso: string }) {
  const moment = formatDateTimeLong(iso)
  return (
    <time dateTime={iso} title={moment} aria-label={moment} className="text-muted-foreground">
      {formatRelatief(iso)}
    </time>
  )
}

/** A day (YYYY-MM-DD) as "16 okt", with the year when it is not this year. */
export function DayCell({ day }: { day: string | null }) {
  return day ? <span className="text-muted-foreground">{formatShortDate(day, todayIso())}</span> : <Dash />
}
